import { useEffect, useState, useCallback, useRef } from "react";
import { dmnStorage } from "../utils/dmnStorage";

const STORAGE_KEY = "intercomVisible";

interface UseIntercomVisibilityReturn {
  isVisible: boolean;
  toggleVisibility: () => void;
  isLoading: boolean;
}

const updateIntercomVisibility = (visible: boolean): void => {
  if (typeof window.Intercom === "function") {
    window.Intercom("update", { hide_default_launcher: !visible });
  }
};

export const useIntercomVisibility = (): UseIntercomVisibilityReturn => {
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const previousUnreadCountRef = useRef<number>(0);
  const hasRegisteredCallbackRef = useRef<boolean>(false);
  const isVisibleRef = useRef<boolean>(true);

  // Keep isVisibleRef in sync with isVisible state
  useEffect(() => {
    isVisibleRef.current = isVisible;
  }, [isVisible]);

  // Register onUnreadCountChange callback to auto-open messenger when
  // new messages arrive while support is hidden
  useEffect(() => {
    if (hasRegisteredCallbackRef.current) return;
    if (typeof window.Intercom !== "function") return;

    window.Intercom("onUnreadCountChange", (unreadCount: number) => {
      // Only auto-open when transitioning from 0 to >0 unread messages
      // and support is currently hidden
      if (
        previousUnreadCountRef.current === 0 &&
        unreadCount > 0 &&
        !isVisibleRef.current
      ) {
        window.Intercom("show");
      }
      previousUnreadCountRef.current = unreadCount;
    });

    hasRegisteredCallbackRef.current = true;
  }, []);

  useEffect(() => {
    const loadStoredVisibility = async (): Promise<void> => {
      const storedValue = await dmnStorage.get(STORAGE_KEY);
      const visibility = storedValue !== false;
      setIsVisible(visibility);
      isVisibleRef.current = visibility;
      setIsLoading(false);
      updateIntercomVisibility(visibility);
    };

    void loadStoredVisibility();
  }, []);

  useEffect(() => {
    if (isLoading) return;
    updateIntercomVisibility(isVisible);
  }, [isVisible, isLoading]);

  const toggleVisibility = useCallback((): void => {
    const newVisibility = !isVisible;
    setIsVisible(newVisibility);
    void dmnStorage.add(STORAGE_KEY, newVisibility);
  }, [isVisible]);

  return {
    isVisible,
    toggleVisibility,
    isLoading
  };
};
